/**
 * Elements d'affichage propres au lead : badges, marqueur d'opposition,
 * definition des colonnes de la vue liste, rendu d'une ligne et d'une carte.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Ban, CalendarClock, Star } from 'lucide-react';
import { LIBELLES_ENRICHISSEMENT, LIBELLES_INTERACTION, nomDepartement } from '@leads/shared';
import type { ChampTriLead, LeadResumeDTO, StatutEnrichissement, SuiviCommercialDTO } from '@leads/shared';
import { Avatar, Badge } from './ui';
import type { TonBadge } from './ui';
import {
  domaineLisible,
  formaterAnciennete,
  formaterCommune,
  formaterDate,
  formaterNombre,
  formaterNote,
  formaterTelephone,
  normaliserUrl,
} from '../lib/format';
import { Contact } from '../lib/contact';
import { ICONES_ECHANGE, LIBELLE_SANS_DATE, etatEcheance, libelleEcheance, texteSansDate } from '../lib/prochaine-etape';

export function BadgeStatut({
  libelle,
  couleur,
}: {
  libelle: string | null | undefined;
  couleur?: string | null;
}) {
  if (!libelle) return <Badge>Sans étape</Badge>;
  return <Badge couleur={couleur ?? undefined}>{libelle}</Badge>;
}

const TONS_ENRICHISSEMENT: Record<StatutEnrichissement, TonBadge> = {
  EN_ATTENTE: 'neutre',
  PARTIEL: 'avertissement',
  COMPLET: 'succes',
  MANUEL_REQUIS: 'info',
  ECHEC: 'danger',
};

export function BadgeEnrichissement({ statut }: { statut: StatutEnrichissement }) {
  return (
    <Badge ton={TONS_ENRICHISSEMENT[statut]} titre="Complétude des données de la fiche">
      {LIBELLES_ENRICHISSEMENT[statut]}
    </Badge>
  );
}

/** Marqueur RGPD : doit rester visible partout ou le lead apparait. */
export function MarqueurOpposition({ compact = false }: { compact?: boolean }) {
  return (
    <span
      title="Opposition enregistrée : ne plus contacter ce lead"
      className="inline-flex items-center gap-1 rounded-full border border-danger-200 bg-danger-50 px-2 py-0.5 text-xs font-medium text-danger-700"
    >
      <Ban aria-hidden="true" size={12} />
      {compact ? <span className="sr-only">Ne plus contacter</span> : 'Ne plus contacter'}
    </span>
  );
}

function Vide() {
  return <span className="text-encre-tenue">—</span>;
}

/** Commercial du lead ; le vivier est nomme, pas laisse vide. */
function CelluleCommercial({ lead }: { lead: LeadResumeDTO }) {
  if (!lead.proprietaire) return <span className="text-sm text-encre-discrete">Vivier</span>;
  return (
    <span className="inline-flex max-w-44 items-center gap-2">
      <Avatar nom={lead.proprietaire.nom} identifiant={lead.proprietaire.id} taille="sm" />
      <span className="truncate">{lead.proprietaire.nom}</span>
    </span>
  );
}

/** Une prochaine etape a montrer : datee, ou texte seul (donnees anterieures a la refonte). */
function aProchaineEtape(suivi: SuiviCommercialDTO | null): boolean {
  return Boolean(suivi?.dateProchaineAction) || texteSansDate(suivi) !== null;
}

/** Prochaine etape : l'icone dit le type, le texte l'echeance ; le retard passe en rouge. */
function CelluleProchaineEtape({ suivi }: { suivi: SuiviCommercialDTO | null }) {
  if (!suivi || !aProchaineEtape(suivi)) return <Vide />;
  const date = suivi.dateProchaineAction;
  const texte = date ? suivi.prochaineAction : texteSansDate(suivi);
  const type = suivi.typeProchaineAction;
  const Icone = type ? ICONES_ECHANGE[type] : CalendarClock;
  const enRetard = date !== null && etatEcheance(date) === 'en-retard';
  return (
    <div className="max-w-52">
      <p className={`inline-flex items-center gap-1.5 whitespace-nowrap ${enRetard ? 'font-medium text-danger-700' : 'text-encre'}`}>
        <Icone aria-hidden="true" size={14} className={enRetard ? '' : 'text-encre-tenue'} />
        <span className="sr-only">{type ? LIBELLES_INTERACTION[type] : 'Prochaine étape'} : </span>
        {date ? libelleEcheance(date) : LIBELLE_SANS_DATE}
      </p>
      {texte && <p className="truncate text-xs text-encre-discrete">{texte}</p>}
    </div>
  );
}

function CelluleTelephone({ lead }: { lead: LeadResumeDTO }) {
  if (!lead.telephone) return <Vide />;
  return (
    <Contact type="telephone" valeur={lead.telephone} lead={lead} className="whitespace-nowrap tabular-nums">
      {formaterTelephone(lead.telephone)}
    </Contact>
  );
}

export interface ColonneLead {
  cle: string;
  libelle: string;
  /** Champ de tri correspondant cote API, si la colonne est triable. */
  tri?: ChampTriLead;
  /**
   * Colonnes de `GET /exports/leads` qui restituent ce que montre la cellule
   * (cles de l'API, `apps/api/src/modules/exports/format.ts`) : une cle
   * inconnue y serait ignoree sans erreur, et manquerait au fichier.
   */
  export: readonly string[];
  /** Colonne affichee par defaut. */
  defaut: boolean;
  droite?: boolean;
  rendu: (lead: LeadResumeDTO) => ReactNode;
}

export const COLONNES_LEAD: readonly ColonneLead[] = [
  {
    // Le SIRET en plus : cle d'unicite du lead, qu'un outil de campagne sait rapprocher.
    cle: 'denomination', libelle: 'Entreprise', tri: 'denomination', export: ['siret', 'denomination', 'enseigne', 'codePostal', 'ville'], defaut: true,
    rendu: (lead) => (
      <div className="min-w-0 max-w-72">
        <div className="flex flex-wrap items-center gap-1.5">
          <Link to={`/leads/${lead.id}`} className="font-medium text-encre hover:text-accent-700">{lead.denomination}</Link>
          {lead.nePlusContacter && <MarqueurOpposition compact />}
        </div>
        <p className="truncate text-xs text-encre-discrete">{[lead.enseigne, formaterCommune(lead.codePostal, lead.ville)].filter(Boolean).join(' · ')}</p>
      </div>
    ),
  },
  {
    cle: 'statut',
    libelle: 'Étape',
    tri: 'statutCommercial',
    export: ['statutCommercial'],
    defaut: true,
    rendu: (lead) => (
      <BadgeStatut libelle={lead.suivi?.statutLibelle} couleur={lead.suivi?.statutCouleur} />
    ),
  },
  { cle: 'proprietaire', libelle: 'Commercial', export: ['proprietaire'], defaut: true, rendu: (lead) => <CelluleCommercial lead={lead} /> },
  {
    cle: 'prochaineAction', libelle: 'Prochaine étape', tri: 'dateProchaineAction', export: ['prochaineAction', 'dateProchaineAction'], defaut: true,
    rendu: (lead) => <CelluleProchaineEtape suivi={lead.suivi} />,
  },
  {
    cle: 'note',
    libelle: 'Note Google',
    tri: 'noteGoogle',
    export: ['noteGoogle', 'nbAvisGoogle'],
    defaut: true,
    droite: true,
    rendu: (lead) =>
      lead.noteGoogle === null ? (
        <Vide />
      ) : (
        <span className="tabular-nums">
          {formaterNote(lead.noteGoogle)}
          <span className="ml-1 text-xs text-encre-discrete">
            ({formaterNombre(lead.nbAvisGoogle ?? 0)})
          </span>
        </span>
      ),
  },
  { cle: 'telephone', libelle: 'Téléphone', export: ['telephone'], defaut: true, rendu: (lead) => <CelluleTelephone lead={lead} /> },
  {
    cle: 'ville', libelle: 'Ville', tri: 'ville', export: ['codePostal', 'ville'], defaut: false,
    rendu: (lead) => (lead.ville ? <span className="whitespace-nowrap">{lead.ville}</span> : <Vide />),
  },
  {
    cle: 'departement',
    libelle: 'Département',
    tri: 'departement',
    export: ['departement'],
    defaut: false,
    rendu: (lead) =>
      lead.departement ? (
        <span title={nomDepartement(lead.departement)}>{lead.departement}</span>
      ) : (
        <Vide />
      ),
  },
  {
    cle: 'dirigeant',
    libelle: 'Dirigeant',
    export: ['dirigeant'],
    defaut: false,
    rendu: (lead) =>
      lead.dirigeantPrincipal ? (
        <span className="whitespace-nowrap">{lead.dirigeantPrincipal}</span>
      ) : (
        <Vide />
      ),
  },
  {
    cle: 'siteWeb',
    libelle: 'Site web',
    export: ['siteWeb'],
    defaut: false,
    rendu: (lead) => {
      const url = normaliserUrl(lead.siteWeb);
      if (!url) return <span className="text-xs font-medium text-avertissement-700">Aucun</span>;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="lien inline-block max-w-32 truncate align-bottom text-xs"
        >
          {domaineLisible(lead.siteWeb)}
        </a>
      );
    },
  },
  {
    cle: 'nbAvis',
    libelle: "Avis Google",
    tri: 'nbAvisGoogle',
    export: ['nbAvisGoogle'],
    defaut: false,
    droite: true,
    rendu: (lead) =>
      lead.nbAvisGoogle === null ? <Vide /> : (
        <span className="tabular-nums">{formaterNombre(lead.nbAvisGoogle)}</span>
      ),
  },
  {
    cle: 'effectif',
    libelle: 'Effectif',
    tri: 'effectif',
    export: ['effectif'],
    defaut: false,
    rendu: (lead) => <span className="text-xs">{lead.trancheEffectifLibelle}</span>,
  },
  {
    cle: 'dateCreation',
    libelle: 'Création',
    tri: 'dateCreation',
    export: ['dateCreation', 'anciennete'],
    defaut: false,
    rendu: (lead) =>
      lead.dateCreation ? (
        <div>
          <p className="tabular-nums">{formaterDate(lead.dateCreation)}</p>
          <p className="text-xs text-encre-discrete">{formaterAnciennete(lead.dateCreation)}</p>
        </div>
      ) : (
        <Vide />
      ),
  },
  {
    cle: 'enrichissement',
    libelle: 'Données',
    export: ['statutEnrichissement'],
    defaut: false,
    rendu: (lead) => <BadgeEnrichissement statut={lead.statutEnrichissement} />,
  },
  {
    cle: 'majLe',
    libelle: 'Mise à jour',
    tri: 'majLe',
    export: ['majLe'],
    defaut: false,
    rendu: (lead) => <span className="tabular-nums text-xs">{formaterDate(lead.majLe)}</span>,
  },
];

export const CLES_COLONNES_DEFAUT: string[] = COLONNES_LEAD.filter((c) => c.defaut).map(
  (c) => c.cle,
);

/** Colonnes d'export (cles de l'API) des colonnes choisies, dans l'ordre du tableau, chacune une fois. */
export function colonnesExport(cles: readonly string[]): string[] {
  return [...new Set(COLONNES_LEAD.filter((colonne) => cles.includes(colonne.cle)).flatMap((colonne) => colonne.export))];
}

/** Ligne de tableau (grands ecrans). */
export function LigneLead({
  lead,
  colonnes,
  selectionne,
  onSelection,
  action,
}: {
  lead: LeadResumeDTO;
  colonnes: readonly ColonneLead[];
  /** Absent lorsque la selection multiple n'est pas proposee. */
  selectionne?: boolean;
  onSelection?: (id: string, selectionne: boolean) => void;
  /** Derniere cellule (bouton « Prendre »…) ; absente, la colonne n'existe pas. */
  action?: ReactNode;
}) {
  return (
    <tr
      /* La teinte d'opposition double le marqueur textuel, elle ne le remplace pas. Les classes
         `ligne-*` la reportent sur la colonne d'actions, collee et opaque (`styles/index.css`). */
      className={
        lead.nePlusContacter ? 'ligne-opposition bg-danger-50/50' : selectionne ? 'ligne-selectionnee bg-accent-50' : undefined
      }
      data-testid={`ligne-lead-${lead.id}`}
    >
      {onSelection ? (
        <td>
          <input
            type="checkbox"
            className="case"
            checked={selectionne ?? false}
            onChange={(evenement) => onSelection(lead.id, evenement.target.checked)}
            aria-label={`Sélectionner ${lead.denomination}`}
          />
        </td>
      ) : null}
      {colonnes.map((colonne) => (
        <td key={colonne.cle} className={colonne.droite ? 'numerique' : undefined}>
          {colonne.rendu(lead)}
        </td>
      ))}
      {action !== undefined && <td className="colonne-actions text-right">{action}</td>}
    </tr>
  );
}

/** Rendu en carte pour les petits ecrans : les memes reperes que la ligne. */
export function CarteLead({ lead, action }: { lead: LeadResumeDTO; action?: ReactNode }) {
  return (
    <li className={`border-b border-neutre-100 p-4 last:border-b-0 ${lead.nePlusContacter ? 'bg-danger-50/50' : ''}`} data-testid={`carte-lead-${lead.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/leads/${lead.id}`} className="text-base font-semibold text-encre hover:text-accent-700">{lead.denomination}</Link>
          <p className="mt-0.5 text-sm text-encre-discrete">{formaterCommune(lead.codePostal, lead.ville)}</p>
        </div>
        <BadgeStatut libelle={lead.suivi?.statutLibelle} couleur={lead.suivi?.statutCouleur} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-encre-douce">
        <CelluleCommercial lead={lead} />
        {aProchaineEtape(lead.suivi) && <CelluleProchaineEtape suivi={lead.suivi} />}
        <CelluleTelephone lead={lead} />
        {lead.noteGoogle !== null && (
          <span className="inline-flex items-center gap-1">
            <Star aria-hidden="true" size={14} className="text-encre-tenue" />{formaterNote(lead.noteGoogle)} ({formaterNombre(lead.nbAvisGoogle ?? 0)})
          </span>
        )}
        {lead.nePlusContacter && <MarqueurOpposition />}
      </div>
      {action !== undefined && <div className="mt-3 empty:hidden">{action}</div>}
    </li>
  );
}
